/**
 * Dyna Agency — Email Service
 *
 * Renders templates client-side using emailTemplates.ts, then invokes the
 * send-email edge function for delivery. The RESEND_API_KEY never touches
 * the browser bundle — it lives only in Supabase vault (Deno env).
 *
 * Usage:
 *   import { sendEmail } from '@/lib/emailService';
 *   await sendEmail({ to: 'client@example.com', templateId: 'welcomeEmail', data: { first_name: 'Avi' }, language: 'he' });
 */

import { supabase } from '@/integrations/supabase/client';
import { EMAIL_TEMPLATES, type TemplateName, type Lang } from './emailTemplates';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface SendEmailOptions {
  /** Recipient address(es). Passed directly to Resend — accepts multiple. */
  to: string | string[];
  /** Key from EMAIL_TEMPLATES registry (e.g. 'welcomeEmail', 'inquiryResponse'). */
  templateId: string;
  /** Merge variables for {{placeholder}} substitution. */
  data: Record<string, string>;
  /** Template language. Defaults to 'en'. */
  language?: Lang;
  /** CRM foreign key — written to email_log. */
  leadId?: string;
  /** CRM foreign key — written to email_log. */
  clientId?: string;
}

export interface SendEmailResult {
  success: boolean;
  error?: string;
}

// ── Core send function ─────────────────────────────────────────────────────────

export async function sendEmail({
  to,
  templateId,
  data,
  language = 'en',
  leadId,
  clientId,
}: SendEmailOptions): Promise<SendEmailResult> {
  // 1. Resolve template from registry
  const fn = EMAIL_TEMPLATES[templateId as TemplateName];
  if (!fn) {
    const msg = `Unknown template: "${templateId}". Available: ${Object.keys(EMAIL_TEMPLATES).join(', ')}`;
    console.error('[emailService]', msg);
    return { success: false, error: msg };
  }

  // 2. Render template to subject + HTML + plain text
  const { subject, html, text } = fn(data, language);

  // 3. Invoke edge function — RESEND_API_KEY stays server-side in Supabase vault
  const { error } = await supabase.functions.invoke('send-email', {
    body: {
      to,
      subject,
      html,
      text,
      from: 'Dyna Agency <hello@dyna-agency.com>',
      templateId,
      leadId:   leadId   ?? null,
      clientId: clientId ?? null,
      language,
    },
  });

  if (error) {
    console.error('[emailService] Edge function error:', error);
    return { success: false, error: error.message };
  }

  return { success: true };
}

// ── Convenience helpers ────────────────────────────────────────────────────────

/**
 * Send to multiple addresses with the same template + data.
 * Useful for 'Both' assignee patterns where two team members get the same email.
 */
export async function sendEmailToMany(
  recipients: string[],
  opts: Omit<SendEmailOptions, 'to'>
): Promise<SendEmailResult[]> {
  return Promise.all(recipients.map(to => sendEmail({ ...opts, to })));
}
